// -----------------------------------------------------------------------------------------------------
// @ AUTH UTILITIES
//
// Methods are derivations of the Auth0 Angular-JWT helper service methods
// https://github.com/auth0/angular2-jwt
// -----------------------------------------------------------------------------------------------------

export class AuthUtils {
    /**
     * Constructor
     */
    constructor() {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Base64 decoder
     * Credits: https://github.com/atk
     *
     * @param str
     * @private
     */
    private static _b64decode(str: string): string {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
        let output = '';

        str = String(str).replace(/=+$/, '');

        if (str.length % 4 === 1) {
            throw new Error(
                '\'atob\' failed: The string to be decoded is not correctly encoded.'
            );
        }

        /* tslint:disable */
        for (
            // initialize result and counters
            let bc = 0, bs: any, buffer: any, idx = 0;
            // get next character
            (buffer = str.charAt(idx++));
            // character found in table? initialize bit storage and add its ascii value;
            ~buffer &&
                (
                    (bs = bc % 4 ? bs * 64 + buffer : buffer),
                    // and if not first of each 4 characters,
                    // convert the first 8 bits to one ascii character
                    bc++ % 4
                )
                ? (output += String.fromCharCode(255 & (bs >> ((-2 * bc) & 6))))
                : 0
        ) {
            // try to find character in table (0-63, not found => -1)
            buffer = chars.indexOf(buffer);
        }
        /* tslint:enable */

        return output;
    }

    /**
     * Base64 unicode decoder
     *
     * @param str
     * @private
     */
    private static _b64DecodeUnicode(str: any): string {
        return decodeURIComponent(
            Array.prototype.map
                .call(this._b64decode(str), (c: any) => {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                })
                .join('')
        );
    }

    /**
     * URL Base 64 decoder
     *
     * @param str
     * @private
     */
    private static _urlBase64Decode(str: string): string {
        let output = str.replace(/-/g, '+').replace(/_/g, '/');
        switch (output.length % 4) {
            case 0:
                {
                    break;
                }
            case 2:
                {
                    output += '==';
                    break;
                }
            case 3:
                {
                    output += '=';
                    break;
                }
            default:
                {
                    throw Error('Illegal base64url string!');
                }
        }
        return this._b64DecodeUnicode(output);
    }

    /**
     * Decode token
     *
     * @param token
     * @private
     */
    private static _decodeToken(token: string): any {
        // Return if there is no token
        if (!token || token === "undefined") {
            return null;
        }

        // Split the token
        const parts = token.split('.');

        if (parts.length !== 3) {
            throw new Error('The inspected token doesn\'t appear to be a JWT. Check to make sure it has three parts and see https://jwt.io for more.');
        }

        // Decode the token using the Base64 decoder
        const decoded = this._urlBase64Decode(parts[1]);

        if (!decoded) {
            throw new Error('Cannot decode the token.');
        }

        return JSON.parse(decoded);
    }

    /**
     * Get token expiration date
     *
     * @param token
     * @private
     */
    private static _getTokenExpirationDate(token: string): Date | null {
        // Get the decoded token
        const decodedToken = this._decodeToken(token);

        // Return if the decodedToken doesn't have an 'exp' field
        if (!decodedToken.hasOwnProperty('exp')) {
            return null;
        }

        // Convert the expiration date
        const date = new Date(0);
        date.setUTCSeconds(decodedToken.exp);

        return date;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Is token expired?
     *
     * @param token
     * @param offsetSeconds
     */
    static isTokenExpired(token: string, offsetSeconds?: number): boolean {
        // Return if there is no token
        if (!token || token === '' || token === "undefined") {
            return true;
        }

        // Get the expiration date
        const date = this._getTokenExpirationDate(token);

        offsetSeconds = offsetSeconds || 0;

        if (date === null) {
            return true;
        }

        // Check if the token is expired
        return !(date.valueOf() > new Date().valueOf() + offsetSeconds * 1000);
    }

    static normalizeRoles(roleSource: any): string[] {
        const roles: string[] = [];

        const appendRoles = (value: any): void => {
            if (Array.isArray(value)) {
                value.forEach((entry) => appendRoles(entry));
                return;
            }

            if (typeof value === 'string') {
                const normalizedRole = value.trim().toUpperCase();
                if (normalizedRole) {
                    roles.push(normalizedRole);
                }
                return;
            }

            if (value && typeof value === 'object') {
                Object.values(value).forEach((entry) => appendRoles(entry));
            }
        };

        appendRoles(roleSource);

        const uniqueRoles = Array.from(new Set(roles));
        const rolePriority = ['@SUPERADMIN', '@ADMIN', '@MANAGER', 'G@MANAGER', 'O@MANAGER', '@USER', 'G@USER', 'O@USER'];

        return uniqueRoles.sort((left, right) => {
            const leftIndex = rolePriority.indexOf(left);
            const rightIndex = rolePriority.indexOf(right);
            const normalizedLeft = leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex;
            const normalizedRight = rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex;

            return normalizedLeft - normalizedRight || left.localeCompare(right);
        });
    }

    static resolveEffectiveRoles(
        roleSource: any,
        options: {
            selectedOrganizationCode?: string | null;
            selectedBranchCode?: string | null;
            fallbackOrganizationCode?: string | null;
            user?: any;
        } = {}
    ): string[] {
        const baseRoles: string[] = [];

        if (typeof roleSource === 'string' || Array.isArray(roleSource)) {
            baseRoles.push(...this.normalizeRoles(roleSource));
        } else if (roleSource && typeof roleSource === 'object') {
            const roleMap = roleSource as Record<string, any>;
            const organizationCodes = Object.keys(roleMap);
            const targetOrganizationCode = options.selectedOrganizationCode || options.fallbackOrganizationCode || organizationCodes[0] || '';

            const appendFromOrganization = (organizationCode?: string): void => {
                if (!organizationCode) {
                    return;
                }

                const organizationRoles = roleMap[organizationCode];
                if (!organizationRoles || typeof organizationRoles !== 'object') {
                    return;
                }

                if (options.selectedBranchCode && Array.isArray(organizationRoles[options.selectedBranchCode])) {
                    baseRoles.push(...this.normalizeRoles(organizationRoles[options.selectedBranchCode]));
                    return;
                }

                if (Array.isArray(organizationRoles._)) {
                    baseRoles.push(...this.normalizeRoles(organizationRoles._));
                    return;
                }

                baseRoles.push(...this.normalizeRoles(organizationRoles));
            };

            appendFromOrganization(targetOrganizationCode);

            if (!baseRoles.length && targetOrganizationCode !== organizationCodes[0]) {
                appendFromOrganization(organizationCodes[0]);
            }

            if (!baseRoles.length) {
                baseRoles.push(...this.normalizeRoles(roleMap));
            }
        }

        

        return this.normalizeRoles(baseRoles);
    }

    static getPrimaryRole(roleSource: any, user?: any): string {
        return this.resolveEffectiveRoles(roleSource, { user })[0] || '';
    }

    static hasRole(roleSource: any, expectedRoles: string | string[], user?: any): boolean {
        const roles = this.resolveEffectiveRoles(roleSource, { user });
        const allowedRoles = this.normalizeRoles(expectedRoles);
        return allowedRoles.some((role) => roles.includes(role));
    }
}
